import { useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { PitchFormValues } from "../Schema/pitchValidationSchema";

interface SelectedDSPs {
  spotify: boolean;
  amazon: boolean;
  deezer: boolean;
  TIDAL: boolean;
  audiomack: boolean;
  soundcloud: boolean;
  apple: boolean;
  youtube: boolean;
  siriusxm: boolean;
  vevo: boolean;
}

interface ProvisionalFieldsControlProps {
  values: PitchFormValues;
  setFieldValue: (field: string, value: any) => void;
  selectedDSPs: SelectedDSPs;
}

interface FieldOption {
  // key sent to backend — "fieldName" for general, "dsp.backendFieldName" for DSP fields
  key: string;
  // Formik form field name — may differ from the backend key for DSP fields
  formikKey: string;
  label: string;
}

interface FieldGroup {
  title: string;
  fields: FieldOption[];
}

// Root-level pitch fields — key === formikKey
const GENERAL_FIELDS: FieldOption[] = [
  { key: "releaseTitle",        formikKey: "releaseTitle",        label: "Release Title" },
  { key: "releaseDate",         formikKey: "releaseDate",         label: "Release Date" },
  { key: "releaseUpc",          formikKey: "releaseUpc",          label: "Release UPC" },
  { key: "focusTrackName",      formikKey: "focusTrackName",      label: "Focus Track Name" },
  { key: "trackIsrc",           formikKey: "trackIsrc",           label: "Track ISRC" },
  { key: "mainGenre",           formikKey: "mainGenre",           label: "Main Genre" },
  { key: "songMood",            formikKey: "songMood",            label: "Song Mood" },
  { key: "language",            formikKey: "language",            label: "Language" },
  { key: "songDescription",     formikKey: "songDescription",     label: "Describe the Song" },
  { key: "shortDescription",    formikKey: "shortDescription",    label: "Description" },
  { key: "expandedDescription", formikKey: "expandedDescription", label: "Expanded Description" },
  { key: "longDescription",     formikKey: "longDescription",     label: "Marketing Description" },
  { key: "lyrics",              formikKey: "lyrics",              label: "Lyrics" },
  { key: "linkToListen",        formikKey: "linkToListen",        label: "Link to Listen" },
  { key: "promoPhotos",         formikKey: "promoPhotos",         label: "Promo Photos" },
  { key: "promoPhotosCredit",   formikKey: "promoPhotosCredit",   label: "Photo Credit" },
  { key: "featuredArtists",     formikKey: "featuredArtists",     label: "Featured Artists" },
  { key: "featuredArtistUri",   formikKey: "featuredArtistUri",   label: "Featured Artist URI" },
  { key: "altTitles",           formikKey: "altTitles",           label: "Alt Titles" },
];

// DSP fields — key is "dspName.backendFieldName", formikKey is the Formik field name
const DSP_FIELD_GROUPS: { dsp: keyof SelectedDSPs; title: string; fields: FieldOption[] }[] = [
  {
    dsp: "spotify",
    title: "Spotify",
    fields: [
      { key: "spotify.spotifyUri",        formikKey: "spotifyUri",        label: "Spotify URI" },
      { key: "spotify.subgenre",          formikKey: "spotifySubgenre",   label: "Spotify Subgenre" },
      { key: "spotify.musicCulture",      formikKey: "musicCulture",      label: "Music Culture" },
      { key: "spotify.instruments",       formikKey: "instruments",       label: "Instruments" },
      { key: "spotify.songStyle",         formikKey: "songStyle",         label: "Song Style" },
      { key: "spotify.playlist",          formikKey: "spotifyPlaylist",   label: "Spotify Playlist" },
      { key: "spotify.artistHometown",    formikKey: "artistHometown",    label: "Artist Hometown" },
      { key: "spotify.artistCurrentTown", formikKey: "artistCurrentTown", label: "Artist Current Town" },
    ],
  },
  {
    dsp: "amazon",
    title: "Amazon Music",
    fields: [
      { key: "amazon.listenersLocation", formikKey: "listenersLocation", label: "Location of Listeners" },
      { key: "amazon.similarArtists",    formikKey: "similarArtists",    label: "Similar Artists" },
      { key: "amazon.songCategory",      formikKey: "songCategory",      label: "Song Category" },
      { key: "amazon.subgenre",          formikKey: "amazonSubgenre",    label: "Amazon Subgenre" },
      { key: "amazon.asin",              formikKey: "asin",              label: "ASIN" },
    ],
  },
  {
    dsp: "deezer",
    title: "Deezer",
    fields: [
      { key: "deezer.priority",          formikKey: "priority",            label: "Deezer Priority" },
      { key: "deezer.editorialTeam",     formikKey: "deezerEditorialTeam", label: "Deezer Editorial Team" },
      { key: "deezer.subgenre",          formikKey: "deezerSubgenre",      label: "Deezer Subgenre" },
      { key: "deezer.playlist",          formikKey: "deezerPlaylist",      label: "Deezer Playlist" },
    ],
  },
  {
    dsp: "TIDAL",
    title: "TIDAL",
    fields: [
      { key: "tidal.artistPageLink",  formikKey: "TIDALpage",          label: "TIDAL Artist Page" },
      { key: "tidal.subgenre",        formikKey: "TIDALSubgenre",      label: "TIDAL Subgenre" },
      { key: "tidal.labelDistributor",formikKey: "LabelDistributor",   label: "Label/Distributor" },
      { key: "tidal.artistLevel",     formikKey: "tidalArtistLevel",   label: "TIDAL Artist Level" },
      { key: "tidal.priorityLevel",   formikKey: "tidalPriorityLevel", label: "TIDAL Priority Level" },
      { key: "tidal.dolbyAtmos",      formikKey: "tidalDolbyAtmos",    label: "Dolby Atmos" },
      { key: "tidal.localMarket",     formikKey: "tidalLocalMarket",   label: "TIDAL Local Market" },
      { key: "tidal.bpm",             formikKey: "tidalBpm",           label: "BPM" },
      { key: "tidal.confirmation",    formikKey: "tidalConfirmation",  label: "TIDAL Confirmation" },
    ],
  },
  {
    dsp: "audiomack",
    title: "Audiomack",
    fields: [
      { key: "audiomack.subgenre", formikKey: "audiomackSubgenre", label: "Audiomack Subgenre" },
    ],
  },
  {
    dsp: "soundcloud",
    title: "SoundCloud",
    fields: [
      { key: "soundcloud.soundCloudProfileUrl",  formikKey: "soundCloudProfileUrl",   label: "SoundCloud Profile URL" },
      { key: "soundcloud.soundCloudContact",     formikKey: "soundCloudContact",      label: "SoundCloud Contact" },
      { key: "soundcloud.subgenre",              formikKey: "soundCloudSubgenre",     label: "SoundCloud Subgenre" },
      { key: "soundcloud.label",                 formikKey: "soundCloudLabel",        label: "Label (SoundCloud)" },
      { key: "soundcloud.distributor",           formikKey: "distributor",            label: "Distributor" },
      { key: "soundcloud.promotionTerritories",  formikKey: "promotionTerritories",   label: "Promotion Territories" },
      { key: "soundcloud.freshMusicFriday",      formikKey: "freshMusicFriday",       label: "Fresh Music Friday" },
      { key: "soundcloud.playlistConsideration", formikKey: "playlistConsideration",  label: "Playlist Consideration" },
      { key: "soundcloud.releaseStage",          formikKey: "soundCloudReleaseStage", label: "Release Stage (SoundCloud)" },
    ],
  },
  {
    dsp: "apple",
    title: "Apple Music",
    fields: [
      { key: "apple.appleId",           formikKey: "appleId",              label: "Apple Artist ID" },
      { key: "apple.appleTrackId",      formikKey: "appleTrackId",         label: "Apple Track ID" },
      { key: "apple.appleAlbumId",      formikKey: "appleAlbumId",         label: "Apple Album ID" },
      { key: "apple.subgenre",          formikKey: "appleSubgenre",        label: "Apple Subgenre" },
      { key: "apple.releaseStage",      formikKey: "appleReleaseStage",    label: "Release Stage (Apple)" },
      { key: "apple.pitchFrom",         formikKey: "applePitchFrom",       label: "Pitch From" },
      { key: "apple.pitchTo",           formikKey: "applePitchTo",         label: "Pitch To" },
      { key: "apple.hasTimedRelease",   formikKey: "appleHasTimedRelease", label: "Has Timed Release" },
      { key: "apple.timedReleaseDate",  formikKey: "appleTimedReleaseDate",label: "Timed Release Date" },
      { key: "apple.hasMotionArtwork",  formikKey: "appleHasMotionArtwork",label: "Has Motion Artwork" },
      { key: "apple.hasSpatialAudio",   formikKey: "appleHasSpatialAudio", label: "Has Spatial Audio" },
      { key: "apple.label",             formikKey: "appleLabel",           label: "Label (Apple)" },
      { key: "apple.promotionDate",     formikKey: "applePromotionDate",   label: "Apple Promotion Date" },
      { key: "apple.country",           formikKey: "appleCountry",         label: "Apple Country" },
    ],
  },
  {
    dsp: "youtube",
    title: "YouTube",
    fields: [
      { key: "youtube.promotionSurface", formikKey: "youtubePromotionSurface", label: "YouTube Promotion Surface" },
      { key: "youtube.songDetails",      formikKey: "youtubeReleaseStage",     label: "Release Stage (YouTube)" },
      { key: "youtube.promotionDate",    formikKey: "youtubePromotionDate",    label: "YouTube Promotion Date" },
      { key: "youtube.territories",      formikKey: "youtubeTerritories",      label: "YouTube Territories" },
    ],
  },
  {
    dsp: "siriusxm",
    title: "SiriusXM",
    fields: [
      { key: "siriusxm.songWriters",     formikKey: "songWriters",     label: "Song Writers" },
      { key: "siriusxm.siriusXmChannel", formikKey: "siriusXmChannel", label: "SiriusXM Channel" },
    ],
  },
  {
    dsp: "vevo",
    title: "Vevo",
    fields: [
      { key: "vevo.contentType",     formikKey: "vevoContentType",     label: "Vevo Content Type" },
      { key: "vevo.premiereDate",    formikKey: "vevoPremiereDate",    label: "Vevo Premiere Date" },
      { key: "vevo.contentProvider", formikKey: "vevoContentProvider", label: "Vevo Content Provider" },
      { key: "vevo.subgenre",        formikKey: "vevoSubgenre",        label: "Vevo Subgenre" },
      { key: "vevo.subgenreOther",   formikKey: "vevoSubgenreOther",   label: "Vevo Subgenre (Other)" },
      { key: "vevo.additionalNotes", formikKey: "vevoAdditionalNotes", label: "Vevo Additional Notes" },
      { key: "vevo.previewLink",     formikKey: "vevoPreviewLink",     label: "Vevo Preview Link" },
      { key: "vevo.rep",             formikKey: "vevoRep",             label: "Vevo Rep" },
      { key: "vevo.repOther",        formikKey: "vevoRepOther",        label: "Vevo Rep (Other)" },
      { key: "vevo.isCtvOnly",       formikKey: "vevoIsCtvOnly",       label: "Vevo CTV Only" },
      { key: "vevo.isRefeature",     formikKey: "vevoIsRefeature",     label: "Vevo Re-feature" },
    ],
  },
];

// Fields no longer offered that older pitches may still have marked
// provisional: TIDAL stopped asking for the artist's country.
const RETIRED_FIELDS: FieldOption[] = [
  { key: "tidal.country", formikKey: "artistCountry", label: "Artist Country" },
];

// key (namespaced) → label
export const PROVISIONAL_FIELD_LABELS: Record<string, string> = [
  ...GENERAL_FIELDS,
  ...DSP_FIELD_GROUPS.flatMap((g) => g.fields),
  ...RETIRED_FIELDS,
].reduce<Record<string, string>>((acc, { key, label }) => {
  acc[key] = label;
  return acc;
}, {});

// namespaced key → Formik field key (for diff logic in EditFormPage)
export const PROVISIONAL_KEY_TO_FORMIK: Record<string, string> = [
  ...GENERAL_FIELDS,
  ...DSP_FIELD_GROUPS.flatMap((g) => g.fields),
  ...RETIRED_FIELDS,
].reduce<Record<string, string>>((acc, { key, formikKey }) => {
  acc[key] = formikKey;
  return acc;
}, {});

export default function ProvisionalFieldsControl({
  values,
  setFieldValue,
  selectedDSPs,
}: ProvisionalFieldsControlProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const provisionalFields: string[] = values.provisionalFields ?? [];
  const provisionalNote: string = values.provisionalNote ?? "";

  const activeDspGroups = DSP_FIELD_GROUPS.filter(g => selectedDSPs[g.dsp]);

  const allGroups: FieldGroup[] = [
    { title: "General", fields: GENERAL_FIELDS },
    ...activeDspGroups,
  ];

  const toggle = (key: string) => {
    const next = provisionalFields.includes(key)
      ? provisionalFields.filter(k => k !== key)
      : [...provisionalFields, key];
    setFieldValue("provisionalFields", next);
  };

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg border transition-colors focus:outline-none focus-visible:border-primary-700 ${
          isExpanded
            ? "border-warning-300 bg-warning-50"
            : "border-warning-200 bg-warning-50 hover:bg-warning-100 hover:border-warning-300"
        }`}
      >
        <div className="flex items-center gap-3">
          <span className="flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-full bg-warning-100">
            <ChevronDownIcon className={`w-4 h-4 text-warning-600 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
          </span>
          <div className="text-left">
            <p className="text-sm font-medium text-warning-900">Need to update something later?</p>
            <p className="text-xs text-warning-700">Flag fields with placeholder values — you can correct them after submission.</p>
          </div>
        </div>
        {provisionalFields.length > 0 && (
          <span className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-warning-200 text-warning-800">
            {provisionalFields.length} field{provisionalFields.length !== 1 ? "s" : ""} flagged
          </span>
        )}
      </button>

      {isExpanded && (
        <div className="mt-3 p-4 bg-warning-50 border border-warning-200 rounded-lg">
          <p className="text-sm text-warning-800 mb-4">
            Check any fields whose final value isn't confirmed yet. You can resubmit with the correct value later.
          </p>

          <div className="space-y-4">
            {allGroups.map(group => (
              <div key={group.title}>
                <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">
                  {group.title}
                </p>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                  {group.fields.map(({ key, label }) => (
                    <label key={key} className="flex items-center gap-2 text-sm text-neutral-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={provisionalFields.includes(key)}
                        onChange={() => toggle(key)}
                        className="w-4 h-4 rounded cursor-pointer text-primary-700 border-neutral-300 focus:border-primary-700"
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Optional note to admin
            </label>
            <div className="relative">
              <textarea
                value={provisionalNote}
                onChange={e => setFieldValue("provisionalNote", e.target.value)}
                maxLength={500}
                rows={3}
                placeholder="E.g. Waiting on the Spotify waterfall UPC — should have it by Friday."
                className="w-full px-3 py-2 text-sm text-black bg-white border rounded-md outline-none transition-colors border-neutral-300 placeholder-neutral-400 focus:border-primary-500 resize-none"
              />
              <div className="absolute bottom-2 right-3 text-xs text-neutral-500">
                {provisionalNote.length}/500
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
